import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiEyeOutline } from "@mdi/js";
import ConfirmationModal from "app/components/common/ConfirmationModal";

// Revoke and Reset Status take the same body — a required structured
// reason_code plus an optional free-text note — so one modal serves both.
// The codes and their labels mirror Contract.VOID_REASON_CODE_CHOICES exactly;
// sending anything else is a 400.
export const REASON_CODES = [
  { value: "fee_changed", label: "Fee changed" },
  {
    value: "inspection_date_or_address_changed",
    label: "Inspection date or address changed",
  },
  { value: "client_information_changed", label: "Client information changed" },
  { value: "inspection_cancelled", label: "Inspection cancelled" },
  { value: "sent_in_error", label: "Sent in error" },
  { value: "other", label: "Other" },
];

// A thin wrapper over the shared ConfirmationModal: this file owns the reason
// form and its validation, the shell (header, footer, Cancel, the loading
// confirm button, the static-while-loading backdrop) comes from common/.
const ContractReasonModal = ({
  show,
  onHide,
  onConfirm,
  title,
  description,
  confirmLabel,
  isLoading = false,
}) => {
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);

  // Reopening for a different action must not inherit the last selection.
  useEffect(() => {
    if (show) {
      setReasonCode("");
      setNote("");
      setTouched(false);
    }
  }, [show]);

  // reason_code is required by the backend, so the modal stays open and marks
  // the field instead of firing a request that is a guaranteed 400.
  const handleConfirm = () => {
    if (!reasonCode) {
      setTouched(true);
      return;
    }
    onConfirm({ reasonCode, note: note.trim() });
  };

  return (
    <ConfirmationModal
      show={show}
      onHide={onHide}
      onConfirm={handleConfirm}
      title={title}
      confirmLabel={confirmLabel}
      isLoading={isLoading}
      loadingLabel="Working"
      staticWhileLoading
    >
      {description && <p className="mt-3 mb-3">{description}</p>}

      <Form.Group className="mb-3" controlId="contract-reason-code">
        <Form.Label>
          Reason <span className="text-danger">*</span>
        </Form.Label>
        <Form.Select
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          isInvalid={touched && !reasonCode}
          disabled={isLoading}
        >
          <option value="">Select a reason</option>
          {REASON_CODES.map((reason) => (
            <option key={reason.value} value={reason.value}>
              {reason.label}
            </option>
          ))}
        </Form.Select>
        {/* Rendered conditionally rather than via Form.Control.Feedback:
            CreateAccount's stylesheet sets an unscoped
            `.invalid-feedback { display: block }`, so Bootstrap's own
            validity-driven visibility never applies app-wide. */}
        {touched && !reasonCode && (
          <div className="jd-reason-error">Choose a reason to continue.</div>
        )}
      </Form.Group>

      <Form.Group controlId="contract-reason-note">
        <Form.Label>Note</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={note}
          maxLength={2000}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add any detail your client should see (optional)"
          disabled={isLoading}
        />
        <Form.Text className="jd-reason-hint">
          <Icon path={mdiEyeOutline} size={0.6} className="me-1" />
          Visible for client
        </Form.Text>
      </Form.Group>
    </ConfirmationModal>
  );
};

export default ContractReasonModal;
